import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import BottomNavBar from '../components/BottomNavBar';
import SEOMeta from '../components/SEOMeta';
import LoginPromptModal from '../components/LoginPromptModal';
import QRISPaymentModal from '../components/QRISPaymentModal';
import { useAuth } from '../contexts/AuthContext';
import { trackViewPricing, trackSelectPackage } from '../utils/analytics';

/* ── Styled ── */
const PageWrapper = styled.div`
  max-width: 512px;
  margin: 0 auto;
  min-height: 100dvh;
  background: #f6f8f8;
  font-family: 'Plus Jakarta Sans', sans-serif;
  padding-bottom: 96px;
`;

const Header = styled.header`
  position: sticky;
  top: 0;
  z-index: 50;
  background: rgba(255,255,255,0.9);
  backdrop-filter: blur(12px);
  border-bottom: 1px solid #f1f5f9;
  padding: 14px 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
`;

const HeaderTitle = styled.h2`
  font-size: 17px;
  font-weight: 700;
  color: #0f172a;
  margin: 0;
`;

const StatusBadge = styled.span<{ active?: boolean }>`
  position: absolute;
  right: 20px;
  padding: 5px 12px;
  background: ${p => p.active ? 'linear-gradient(135deg, #2DBCAF, #059669)' : '#f1f5f9'};
  color: ${p => p.active ? 'white' : '#64748b'};
  font-size: 11px;
  font-weight: 700;
  border-radius: 9999px;
  letter-spacing: 0.3px;
`;

/* Active Premium Section */
const ActivePremiumSection = styled.section`
  padding: 32px 24px;
  text-align: center;
`;

const ActiveCard = styled.div`
  background: linear-gradient(135deg, #2DBCAF 0%, #059669 100%);
  border-radius: 24px;
  padding: 32px 24px;
  color: white;
  margin-bottom: 24px;
  box-shadow: 0 12px 40px rgba(45,188,175,0.3);
`;

const ActiveIcon = styled.div`
  font-size: 48px;
  margin-bottom: 16px;
`;

const ActiveTitle = styled.h2`
  font-size: 24px;
  font-weight: 800;
  margin: 0 0 8px;
`;

const ActiveSubtitle = styled.p`
  font-size: 14px;
  opacity: 0.9;
  margin: 0 0 24px;
`;

const ActiveInfo = styled.div`
  background: rgba(255,255,255,0.15);
  border-radius: 14px;
  padding: 16px;
  text-align: left;
`;

const ActiveInfoRow = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 8px 0;
  border-bottom: 1px solid rgba(255,255,255,0.1);
  
  &:last-child {
    border-bottom: none;
  }
`;

const ActiveInfoLabel = styled.span`
  font-size: 13px;
  opacity: 0.8;
`;

const ActiveInfoValue = styled.span`
  font-size: 14px;
  font-weight: 700;
`;

const ActiveBenefits = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: 24px;
`;

const ActiveBenefitItem = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  background: white;
  border-radius: 12px;
  padding: 14px 16px;
  text-align: left;
  
  .icon {
    font-size: 20px;
  }
  
  span:last-child {
    font-size: 14px;
    font-weight: 600;
    color: #0f172a;
  }
`;

const ExtendButton = styled.button`
  width: 100%;
  height: 52px;
  border-radius: 14px;
  background: white;
  color: #2DBCAF;
  font-size: 15px;
  font-weight: 700;
  border: none;
  cursor: pointer;
  margin-top: 20px;
  
  &:active {
    transform: scale(0.98);
  }
`;

/* Hero */
const HeroSection = styled.section`
  padding: 32px 24px 16px;
  text-align: center;
`;

const CrownCircle = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: rgba(45,188,175,0.1);
  margin-bottom: 20px;
  .material-symbols-outlined {
    font-size: 44px;
    color: #2DBCAF;
    font-variation-settings: 'FILL' 1, 'wght' 400, 'GRAD' 0, 'opsz' 48;
  }
`;

const HeroTitle = styled.h1`
  font-size: 30px;
  font-weight: 800;
  color: #0f172a;
  line-height: 1.2;
  letter-spacing: -0.5px;
  margin: 0 0 12px 0;
`;

const HeroDesc = styled.p`
  font-size: 16px;
  color: #64748b;
  margin: 0 0 24px 0;
  line-height: 1.6;
`;

const HeroImgBox = styled.div`
  position: relative;
  width: 100%;
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 12px 40px rgba(45,188,175,0.2);
  margin-bottom: 8px;
  background: #fce4ec;
  img { width: 100%; height: auto; display: block; }
`;

/* Report */
const ReportSection = styled.section`
  padding: 24px;
  background: white;
  border-radius: 20px 20px 0 0;
  margin: 8px 0 0;
`;

const SectionTitle = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
  h3 { font-size: 18px; font-weight: 700; color: #0f172a; margin: 0; }
  .material-symbols-outlined {
    font-size: 22px;
    color: #2DBCAF;
    font-variation-settings: 'FILL' 1, 'wght' 400, 'GRAD' 0, 'opsz' 24;
  }
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 10px;
  margin-bottom: 16px;
`;

const StatCard = styled.div`
  background: rgba(45,188,175,0.05);
  border: 1px solid rgba(45,188,175,0.12);
  border-radius: 14px;
  padding: 14px 8px;
  text-align: center;
  p:first-child { font-size: 11px; color: #64748b; margin: 0 0 4px 0; }
  p:last-child { font-size: 22px; font-weight: 800; color: #2DBCAF; margin: 0; }
`;

const BarChart = styled.div`
  width: 100%;
  height: 120px;
  background: #f8fafc;
  border-radius: 14px;
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  padding: 12px;
  gap: 8px;
`;

const Bar = styled.div<{ h: string; opacity?: string }>`
  flex: 1;
  background: ${p => p.opacity ? `rgba(45,188,175,${p.opacity})` : '#2DBCAF'};
  border-radius: 6px 6px 0 0;
  height: ${p => p.h};
`;

const ReportCaption = styled.p`
  font-size: 12px;
  color: #94a3b8;
  text-align: center;
  font-style: italic;
  margin: 10px 0 0;
`;

/* Benefits */
const BenefitsSection = styled.section`
  padding: 24px;
  background: white;
  margin-top: 2px;
`;

const BenefitGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 12px;
  margin-top: 16px;
`;

const BenefitCard = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 14px;
  padding: 16px;
  background: #f8fafc;
  border: 1px solid #f1f5f9;
  border-radius: 16px;
`;

const BenefitIcon = styled.div`
  background: rgba(45,188,175,0.1);
  padding: 10px;
  border-radius: 12px;
  .material-symbols-outlined {
    font-size: 22px;
    color: #2DBCAF;
    font-variation-settings: 'FILL' 1, 'wght' 400, 'GRAD' 0, 'opsz' 24;
    display: block;
  }
`;

const BenefitText = styled.div`
  h4 { font-size: 14px; font-weight: 700; color: #0f172a; margin: 0 0 4px 0; }
  p { font-size: 12px; color: #64748b; margin: 0; line-height: 1.5; }
`;

/* Pricing */
const PricingSection = styled.section`
  padding: 24px;
  background: #f6f8f8;
`;

const PricingTitle = styled.h3`
  font-size: 18px;
  font-weight: 700;
  color: #0f172a;
  text-align: center;
  margin: 0 0 20px 0;
`;

const MonthlyCard = styled.div`
  padding: 20px;
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 18px;
  margin-bottom: 16px;
`;

const PlanHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 16px;
  h4 { font-size: 16px; font-weight: 700; color: #0f172a; margin: 0; }
  p { font-size: 12px; color: #64748b; margin: 4px 0 0; }
`;

const PlanPrice = styled.p`
  font-size: 20px;
  font-weight: 800;
  color: #0f172a;
  margin: 0;
`;

const PlanBtn = styled.button<{ variant?: 'primary' | 'accent' }>`
  width: 100%;
  height: 52px;
  border-radius: 9999px;
  font-size: 15px;
  font-weight: 700;
  font-family: inherit;
  border: none;
  cursor: pointer;
  background: ${p => p.variant === 'accent' ? '#FFA929' : '#f1f5f9'};
  color: ${p => p.variant === 'accent' ? 'white' : '#0f172a'};
  box-shadow: ${p => p.variant === 'accent' ? '0 6px 20px rgba(255,169,41,0.3)' : 'none'};
  &:active { transform: scale(0.97); }
`;

const YearlyCard = styled.div`
  position: relative;
  padding: 24px 20px 20px;
  background: white;
  border: 3px solid #FFA929;
  border-radius: 18px;
  box-shadow: 0 8px 30px rgba(255,169,41,0.15);
  margin-top: 20px;
`;

const BestValueBadge = styled.div`
  position: absolute;
  top: -12px;
  left: 50%;
  transform: translateX(-50%);
  background: linear-gradient(135deg, #FFA929 0%, #FF8C00 100%);
  color: white;
  font-size: 11px;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  padding: 6px 16px;
  border-radius: 20px;
  white-space: nowrap;
  box-shadow: 0 2px 8px rgba(255, 140, 0, 0.3);
`;

const YearlyPrice = styled.div`
  text-align: right;
  p:first-child { font-size: 20px; font-weight: 800; color: #FFA929; margin: 0; }
  p:last-child { font-size: 11px; color: #94a3b8; text-decoration: line-through; margin: 2px 0 0; }
`;

const YearlyList = styled.ul`
  list-style: none;
  padding: 0; margin: 0 0 16px 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const YearlyItem = styled.li`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: #475569;
  .material-symbols-outlined {
    font-size: 18px;
    color: #FFA929;
    font-variation-settings: 'FILL' 0, 'wght' 400, 'GRAD' 0, 'opsz' 24;
  }
`;

/* Payment Modal */
const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.5);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: flex-end;
  justify-content: center;
  z-index: 200;
`;

const ModalSheet = styled.div`
  background: white;
  border-radius: 24px 24px 0 0;
  padding: 24px;
  width: 100%;
  max-width: 512px;
  max-height: 90vh;
  overflow-y: auto;
  font-family: 'Plus Jakarta Sans', sans-serif;
  animation: slideUp 0.3s ease-out;
  @keyframes slideUp {
    from { transform: translateY(100%); }
    to { transform: translateY(0); }
  }
`;

const ModalHandle = styled.div`
  width: 40px;
  height: 4px;
  background: #e2e8f0;
  border-radius: 2px;
  margin: 0 auto 20px;
`;

const ModalTitle = styled.h3`
  font-size: 20px;
  font-weight: 800;
  color: #0f172a;
  margin: 0 0 8px;
  text-align: center;
`;

const ModalSubtitle = styled.p`
  font-size: 14px;
  color: #64748b;
  margin: 0 0 24px;
  text-align: center;
`;

const PaymentBox = styled.div`
  background: #f8fafc;
  border-radius: 16px;
  padding: 20px;
  margin-bottom: 16px;
`;

const PaymentRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
  &:last-child { margin-bottom: 0; }
`;

const PaymentLabel = styled.span`
  font-size: 13px;
  color: #64748b;
`;

const PaymentValue = styled.span<{ large?: boolean; accent?: boolean; discount?: boolean }>`
  font-size: ${p => p.large ? '18px' : '14px'};
  font-weight: ${p => p.large ? '800' : '600'};
  color: ${p => p.discount ? '#ef4444' : p.accent ? '#2DBCAF' : '#0f172a'};
`;

/* Voucher Input */
const VoucherBox = styled.div`
  margin-bottom: 16px;
`;

const VoucherInputRow = styled.div`
  display: flex;
  gap: 8px;
`;

const VoucherInput = styled.input`
  flex: 1;
  height: 44px;
  padding: 0 14px;
  border: 2px solid #e2e8f0;
  border-radius: 10px;
  font-size: 14px;
  font-family: inherit;
  text-transform: uppercase;
  &:focus { outline: none; border-color: #2DBCAF; }
  &::placeholder { text-transform: none; }
`;

const VoucherBtn = styled.button<{ loading?: boolean }>`
  height: 44px;
  padding: 0 16px;
  border: none;
  border-radius: 10px;
  background: #2DBCAF;
  color: white;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  opacity: ${p => p.loading ? 0.7 : 1};
  &:disabled { background: #94a3b8; cursor: not-allowed; }
`;

const VoucherSuccess = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: #dcfce7;
  border: 1px solid #86efac;
  border-radius: 10px;
  padding: 12px 14px;
  margin-top: 8px;
`;

const VoucherSuccessText = styled.span`
  font-size: 13px;
  font-weight: 600;
  color: #166534;
`;

const VoucherRemoveBtn = styled.button`
  background: none;
  border: none;
  color: #dc2626;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
`;

const VoucherError = styled.div`
  font-size: 12px;
  color: #dc2626;
  margin-top: 8px;
`;

const BankBox = styled.div`
  background: linear-gradient(135deg, #0060AF 0%, #003D79 100%);
  border-radius: 16px;
  padding: 20px;
  color: white;
  margin-bottom: 16px;
`;

const BankLabel = styled.div`
  font-size: 11px;
  opacity: 0.8;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 4px;
`;

const BankValue = styled.div`
  font-size: 16px;
  font-weight: 700;
  margin-bottom: 12px;
`;

const BankAccountNumber = styled.div`
  font-size: 24px;
  font-weight: 800;
  letter-spacing: 2px;
  margin-bottom: 4px;
`;

const CopyButton = styled.button`
  background: rgba(255,255,255,0.2);
  border: none;
  border-radius: 8px;
  padding: 8px 16px;
  color: white;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 12px;
  &:active { background: rgba(255,255,255,0.3); }
`;

const CopySmallButton = styled.button`
  background: rgba(45,188,175,0.1);
  border: none;
  border-radius: 6px;
  padding: 4px 8px;
  font-size: 12px;
  cursor: pointer;
  &:active { background: rgba(45,188,175,0.2); }
`;

const WarningBox = styled.div`
  background: #fef3c7;
  border: 1px solid #fde68a;
  border-radius: 12px;
  padding: 14px 16px;
  margin-bottom: 20px;
  display: flex;
  align-items: flex-start;
  gap: 10px;
  span.icon { font-size: 18px; }
  p { font-size: 12px; color: #92400e; margin: 0; line-height: 1.5; }
`;

const WhatsAppButton = styled.button`
  width: 100%;
  height: 56px;
  border-radius: 14px;
  font-size: 16px;
  font-weight: 700;
  font-family: inherit;
  border: none;
  cursor: pointer;
  background: #25D366;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  box-shadow: 0 4px 14px rgba(37,211,102,0.3);
  &:active { transform: scale(0.98); }
`;

const CloseButton = styled.button`
  width: 100%;
  height: 48px;
  border-radius: 12px;
  font-size: 14px;
  font-weight: 600;
  font-family: inherit;
  border: 1px solid #e2e8f0;
  cursor: pointer;
  background: white;
  color: #64748b;
  margin-top: 12px;
`;

const PaymentMethodBtn = styled.button`
  width: 100%;
  padding: 16px 20px;
  border-radius: 16px;
  font-size: 14px;
  font-family: inherit;
  border: 2px solid #e2e8f0;
  cursor: pointer;
  background: white;
  display: flex;
  align-items: center;
  text-align: left;
  transition: all 0.2s;
  &:hover { border-color: #2DBCAF; }
  &:active { transform: scale(0.98); }
`;

const BENEFITS = [
  { icon: 'sports_esports', title: 'Akses Semua Game', desc: 'Mainkan ratusan koleksi game edukasi tanpa terkunci.' },
  { icon: 'block', title: 'Bebas Iklan', desc: 'Pengalaman belajar yang fokus tanpa gangguan iklan.' },
  { icon: 'child_care', title: 'Akses Fitur Tumbuh Kembang', desc: 'Stimulasi dan pantau perkembangan anak dengan lengkap.' },
];

type PackageType = 'monthly' | 'yearly' | 'fouryear';

const PACKAGES: Record<PackageType, { name: string; price: number; originalPrice: number; duration: string; days: number }> = {
  monthly: { name: 'Premium 1 Bulan', price: 20000, originalPrice: 50000, duration: '1 bulan', days: 30 },
  yearly: { name: 'Premium Setahun', price: 50000, originalPrice: 100000, duration: '12 bulan', days: 365 },
  fouryear: { name: 'Premium 4 Tahun', price: 200000, originalPrice: 400000, duration: '4 tahun', days: 1460 },
};

const BANK_INFO = {
  bank: 'BCA',
  accountNumber: '8841014499',
  accountName: 'Bramastana Dewangga',
};

const WA_NUMBER = '6287788116467';

/* ── Component ── */
export default function PremiumPage() {
  const { user, refreshUser } = useAuth();
  const [showPayment, setShowPayment] = useState(false);
  const [showQRIS, setShowQRIS] = useState(false);
  const [showLoginPrompt, setShowLoginPrompt] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState<PackageType>('yearly');
  const [copiedAccount, setCopiedAccount] = useState(false);
  const [copiedAmount, setCopiedAmount] = useState(false);

  // Voucher state
  const [voucherCode, setVoucherCode] = useState('');
  const [voucherLoading, setVoucherLoading] = useState(false);
  const [voucherError, setVoucherError] = useState('');
  const [appliedVoucher, setAppliedVoucher] = useState<{
    code: string;
    discount: number;
  } | null>(null);

  const generateOrderId = () => {
    const now = new Date();
    const date = now.toISOString().slice(0, 10).replace(/-/g, '');
    const time = now.getTime().toString().slice(-4);
    return `KG-${date}-${time}`;
  };

  const generateUniqueCode = () => Math.floor(Math.random() * 900) + 100;

  const [orderId] = useState(generateOrderId);
  const [uniqueCode] = useState(generateUniqueCode);

  // Check if user has active premium
  const isPremium = user?.subscription?.status === 'active' && 
    user?.subscription?.endDate && 
    new Date(user.subscription.endDate) > new Date();

  // Track view pricing on page load
  useEffect(() => {
    if (!isPremium) {
      trackViewPricing();
    }
  }, [isPremium]);

  const handleSelectPackage = (pkg: PackageType) => {
    setSelectedPackage(pkg);
    // Reset voucher when changing package
    setAppliedVoucher(null);
    setVoucherCode('');
    setVoucherError('');
    
    // Track package selection
    trackSelectPackage(pkg);
    
    if (!user) {
      setShowLoginPrompt(true);
      return;
    }
    // Langsung buka QRIS modal
    setShowQRIS(true);
  };

  const handleApplyVoucher = async () => {
    if (!voucherCode.trim()) return;
    
    setVoucherLoading(true);
    setVoucherError('');
    
    try {
      const res = await fetch('/api/vouchers/validate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          code: voucherCode.toUpperCase(),
          package: selectedPackage,
        }),
      });
      
      const data = await res.json();
      
      if (!res.ok || !data.valid) {
        setVoucherError(data.error || 'Voucher tidak valid');
        return;
      }
      
      setAppliedVoucher({
        code: data.code,
        discount: data.discount,
      });
      setVoucherError('');
    } catch {
      setVoucherError('Gagal memvalidasi voucher');
    } finally {
      setVoucherLoading(false);
    }
  };

  const handleRemoveVoucher = () => {
    setAppliedVoucher(null);
    setVoucherCode('');
    setVoucherError('');
  };

  const handleCopyAccount = () => {
    navigator.clipboard.writeText(BANK_INFO.accountNumber);
    setCopiedAccount(true);
    setTimeout(() => setCopiedAccount(false), 2000);
  };

  const handleCopyAmount = () => {
    navigator.clipboard.writeText(totalAmount.toString());
    setCopiedAmount(true);
    setTimeout(() => setCopiedAmount(false), 2000);
  };

  const handleWhatsApp = () => {
    const pkg = PACKAGES[selectedPackage];
    let message = `Halo Admin Kindergame 👋

Saya sudah transfer untuk:
📦 Paket: ${pkg.name}
💰 Nominal: Rp ${totalAmount.toLocaleString('id-ID')}`;
    
    if (appliedVoucher) {
      message += `\n🎟️ Voucher: ${appliedVoucher.code} (- Rp ${appliedVoucher.discount.toLocaleString('id-ID')})`;
    }
    
    message += `
🆔 Order ID: ${orderId}
📧 Email: ${user?.email || '-'}
👤 Nama: ${user?.name || '-'}

*Bukti transfer terlampir*

Mohon diaktifkan ya, terima kasih! 🙏`;

    const waUrl = `https://wa.me/${WA_NUMBER}?text=${encodeURIComponent(message)}`;
    window.open(waUrl, '_blank');
  };

  const pkg = PACKAGES[selectedPackage];
  const basePrice = pkg.price;
  const discount = appliedVoucher?.discount || 0;
  const priceAfterDiscount = basePrice - discount;
  const totalAmount = priceAfterDiscount + uniqueCode;

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('id-ID', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  };

  return (
    <>
      <SEOMeta
        title="Premium - Akses Semua Game Edukasi Tanpa Batas"
        description="Berlangganan KinderGame Premium mulai Rp 20.000/bulan. Akses 200+ game eksklusif, bebas iklan, laporan perkembangan belajar anak secara detail."
        path="/premium"
      />
      <PageWrapper>
        <Header>
          <HeaderTitle>Premium</HeaderTitle>
          <StatusBadge active={isPremium}>
            {isPremium ? '✨ AKTIF' : 'TIDAK AKTIF'}
          </StatusBadge>
        </Header>

        {isPremium ? (
          /* ── PREMIUM ACTIVE VIEW ── */
          <ActivePremiumSection>
            <ActiveCard>
              <ActiveIcon>🎉</ActiveIcon>
              <ActiveTitle>Selamat!</ActiveTitle>
              <ActiveSubtitle>Kamu sudah menjadi member Premium</ActiveSubtitle>
              
              <ActiveInfo>
                <ActiveInfoRow>
                  <ActiveInfoLabel>Paket</ActiveInfoLabel>
                  <ActiveInfoValue>
                    {user?.subscription?.type === 'fouryear' ? '4 Tahun' : 
                     user?.subscription?.type === 'yearly' ? 'Setahun' : 
                     user?.subscription?.type === 'monthly' ? '1 Bulan' : 'Premium'}
                  </ActiveInfoValue>
                </ActiveInfoRow>
                <ActiveInfoRow>
                  <ActiveInfoLabel>Aktif sampai</ActiveInfoLabel>
                  <ActiveInfoValue>
                    {user?.subscription?.endDate ? formatDate(user.subscription.endDate) : '-'}
                  </ActiveInfoValue>
                </ActiveInfoRow>
              </ActiveInfo>
              
              {/* Perpanjang langganan - hidden for now
              <ExtendButton onClick={() => setShowPayment(true)}>
                Perpanjang Langganan
              </ExtendButton>
              */}
            </ActiveCard>
            
            <ActiveBenefits>
              <ActiveBenefitItem>
                <span className="icon">✅</span>
                <span>Akses Semua Game</span>
              </ActiveBenefitItem>
              <ActiveBenefitItem>
                <span className="icon">✅</span>
                <span>Bebas Iklan</span>
              </ActiveBenefitItem>
              <ActiveBenefitItem>
                <span className="icon">✅</span>
                <span>Akses Fitur Tumbuh Kembang</span>
              </ActiveBenefitItem>
            </ActiveBenefits>
          </ActivePremiumSection>
        ) : (
          /* ── NOT PREMIUM VIEW ── */
          <>
            <HeroSection>
              <CrownCircle>
                <span className="material-symbols-outlined">crown</span>
              </CrownCircle>
              <HeroTitle>Buka Potensi Penuh &amp; Belajar Tanpa Batas!</HeroTitle>
              <HeroDesc>
                Dapatkan akses eksklusif ke semua fitur pendidikan terbaik untuk pertumbuhan buah hati Anda.
              </HeroDesc>
              <HeroImgBox>
                <img src="/assets/images/mascot-premium.png?v=2" alt="Maskot KinderGame Premium" />
              </HeroImgBox>
            </HeroSection>

            <ReportSection>
              <SectionTitle>
                <h3>Laporan Belajar Anak</h3>
                <span className="material-symbols-outlined">insights</span>
              </SectionTitle>
              <StatsGrid>
                <StatCard><p>Huruf</p><p>85%</p></StatCard>
                <StatCard><p>Angka</p><p>92%</p></StatCard>
                <StatCard><p>Logika</p><p>78%</p></StatCard>
              </StatsGrid>
              <BarChart>
                <Bar h="40%" opacity="0.2" />
                <Bar h="70%" opacity="0.4" />
                <Bar h="90%" />
                <Bar h="55%" opacity="0.6" />
                <Bar h="30%" opacity="0.3" />
                <Bar h="85%" opacity="0.8" />
              </BarChart>
              <ReportCaption>Pantau perkembangan si kecil secara detail setiap minggu.</ReportCaption>
            </ReportSection>

            <BenefitsSection>
              <SectionTitle><h3>Keuntungan Premium</h3></SectionTitle>
              <BenefitGrid>
                {BENEFITS.map(b => (
                  <BenefitCard key={b.title}>
                    <BenefitIcon>
                      <span className="material-symbols-outlined">{b.icon}</span>
                    </BenefitIcon>
                    <BenefitText>
                      <h4>{b.title}</h4>
                      <p>{b.desc}</p>
                    </BenefitText>
                  </BenefitCard>
                ))}
              </BenefitGrid>
            </BenefitsSection>

            <PricingSection>
              <PricingTitle>Pilih Paket Belajar</PricingTitle>

              {/* 1 Bulan */}
              <MonthlyCard>
                <PlanHeader>
                  <div>
                    <h4>1 Bulan</h4>
                    <p>Akses penuh selama 30 hari</p>
                  </div>
                  <YearlyPrice>
                    <p>Rp 20rb</p>
                    <p>Rp 50rb</p>
                  </YearlyPrice>
                </PlanHeader>
                <PlanBtn onClick={() => handleSelectPackage('monthly')}>Pilih Paket</PlanBtn>
              </MonthlyCard>

              {/* Setahun - Paling Laris */}
              <YearlyCard>
                <BestValueBadge>🔥 Paling Laris</BestValueBadge>
                <PlanHeader>
                  <div>
                    <h4>Setahun</h4>
                    <p>Akses penuh selama 12 bulan</p>
                  </div>
                  <YearlyPrice>
                    <p>Rp 50rb</p>
                    <p>Rp 100rb</p>
                  </YearlyPrice>
                </PlanHeader>
                <YearlyList>
                  <YearlyItem>
                    <span className="material-symbols-outlined">check_circle</span>
                    Hemat 50% dari harga normal
                  </YearlyItem>
                  <YearlyItem>
                    <span className="material-symbols-outlined">check_circle</span>
                    Bisa untuk lebih dari 1 profil anak
                  </YearlyItem>
                </YearlyList>
                <PlanBtn variant="accent" onClick={() => handleSelectPackage('yearly')}>
                  Berlangganan Sekarang
                </PlanBtn>
              </YearlyCard>

              {/* 4 Tahun - Paling Hemat */}
              <YearlyCard style={{ borderColor: '#10B981' }}>
                <BestValueBadge style={{ background: 'linear-gradient(135deg, #10B981, #059669)' }}>💎 Paling Hemat</BestValueBadge>
                <PlanHeader>
                  <div>
                    <h4>4 Tahun</h4>
                    <p>Akses penuh selama 4 tahun</p>
                  </div>
                  <YearlyPrice>
                    <p>Rp 200rb</p>
                    <p>Rp 400rb</p>
                  </YearlyPrice>
                </PlanHeader>
                <YearlyList>
                  <YearlyItem>
                    <span className="material-symbols-outlined">check_circle</span>
                    Hemat 50% dari harga normal
                  </YearlyItem>
                  <YearlyItem>
                    <span className="material-symbols-outlined">check_circle</span>
                    Bisa untuk lebih dari 1 profil anak
                  </YearlyItem>
                </YearlyList>
                <PlanBtn variant="accent" style={{ background: 'linear-gradient(135deg, #10B981, #059669)' }} onClick={() => handleSelectPackage('fouryear')}>
                  Berlangganan Sekarang
                </PlanBtn>
              </YearlyCard>
            </PricingSection>
          </>
        )}
      </PageWrapper>

      <BottomNavBar />

      {/* Login Prompt Modal */}
      {showLoginPrompt && (
        <LoginPromptModal
          title="Login untuk Berlangganan"
          subtitle="Silakan login terlebih dahulu untuk melanjutkan pembayaran Premium."
          onClose={() => setShowLoginPrompt(false)}
        />
      )}

      

      {/* QRIS Payment Modal */}
      {showQRIS && (
        <QRISPaymentModal
          packageType={selectedPackage}
          packageName={PACKAGES[selectedPackage].name}
          voucherCode={appliedVoucher?.code}
          onClose={() => setShowQRIS(false)}
          onSuccess={() => {
            setShowQRIS(false);
            refreshUser?.();
          }}
        />
      )}
    </>
  );
}
